import { useState, useEffect } from "react";
import type { Product } from "@/types";
import { createProductRequestSchema, updateProductRequestSchema } from "@/validation";
import { zodToFieldErrors, firstFieldErrors } from "@trillion/validation";
import { validateJsonText } from "@/lib/json-utils";
import { useNetworkOptions } from "@/hooks/use-network-options";
import { useBrandOptions } from "@/hooks/use-brand-options";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Switch } from "@trillion/ui/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { Loader2 } from "lucide-react";

interface ProductFormData {
  source_id: string; // UI state only — drives the brand cascade; never sent in the payload
  brand_id: string;
  external_id: string;
  country_code: string;
  language: string;
  name: string;
  category: string;
  description: string;
  price: string;
  currency: string;
  original_price: string;
  url: string;
  tracking_url: string;
  image_url: string;
  rating: string;
  rating_count: string;
  in_stock: boolean;
  active: boolean;
  specs: string;
  metadata: string;
}

interface ProductFormProps {
  initialData?: Product;
  onSubmit: (data: Partial<Product>) => Promise<void>;
  isLoading: boolean;
}

function toFormData(product?: Product): ProductFormData {
  if (!product) {
    return {
      source_id: "",
      brand_id: "",
      external_id: "",
      country_code: "",
      language: "",
      name: "",
      category: "",
      description: "",
      price: "",
      currency: "",
      original_price: "",
      url: "",
      tracking_url: "",
      image_url: "",
      rating: "",
      rating_count: "",
      in_stock: true,
      active: true,
      specs: "",
      metadata: "",
    };
  }
  return {
    source_id: String(product.source_id),
    brand_id: String(product.brand_id),
    external_id: product.external_id,
    country_code: product.country_code,
    language: product.language ?? "",
    name: product.name,
    category: product.category ?? "",
    description: product.description ?? "",
    price: product.price != null ? String(product.price) : "",
    currency: product.currency ?? "",
    original_price: product.original_price != null ? String(product.original_price) : "",
    url: product.url,
    tracking_url: product.tracking_url ?? "",
    image_url: product.image_url ?? "",
    rating: product.rating != null ? String(product.rating) : "",
    rating_count: product.rating_count != null ? String(product.rating_count) : "",
    in_stock: product.in_stock,
    active: product.active,
    specs: product.specs ?? "",
    metadata: product.metadata ?? "",
  };
}

export function ProductForm({ initialData, onSubmit, isLoading }: ProductFormProps) {
  const [form, setForm] = useState<ProductFormData>(() => toFormData(initialData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { networks, isLoading: networksLoading } = useNetworkOptions();
  const selectedSourceId = form.source_id ? Number(form.source_id) : null;
  const { brands, isLoading: brandsLoading } = useBrandOptions(selectedSourceId);
  const isEdit = initialData != null;

  useEffect(() => {
    if (initialData) {
      setForm(toFormData(initialData));
    }
  }, [initialData]);

  function updateField(field: keyof ProductFormData, value: string | boolean) {
    // country_code / currency are validated UPPERCASE (^[A-Z]{2}$ / ^[A-Z]{3}$) — uppercase on input
    // so natural typing passes and the stored market key matches search normalization.
    const next =
      (field === "country_code" || field === "currency") && typeof value === "string"
        ? value.toUpperCase()
        : value;
    setForm((prev) => ({ ...prev, [field]: next }));
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
  }

  // Changing network resets the brand — the payload must never carry a brand from another network.
  function handleNetworkChange(value: string) {
    setForm((prev) => ({ ...prev, source_id: value, brand_id: "" }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.source_id;
      delete next.brand_id;
      return next;
    });
  }

  function buildPayload() {
    // House rule: non-required fields serialize as null (never "" or omission) — the schemas are
    // .nullable().optional(); server updates are null-guarded (null = leave unchanged).
    return {
      brand_id: Number(form.brand_id),
      external_id: form.external_id.trim(),
      country_code: form.country_code.trim(),
      // Required on create (products.language is NOT NULL); on update, null = leave unchanged.
      language: form.language.trim() || null,
      name: form.name.trim(),
      category: form.category.trim() || null,
      description: form.description.trim() || null,
      price: form.price.trim() ? Number(form.price) : null,
      currency: form.currency.trim() || null,
      original_price: form.original_price.trim() ? Number(form.original_price) : null,
      url: form.url.trim(),
      tracking_url: form.tracking_url.trim() || null,
      image_url: form.image_url.trim() || null,
      rating: form.rating.trim() ? Number(form.rating) : null,
      rating_count: form.rating_count.trim() ? Number(form.rating_count) : null,
      in_stock: form.in_stock,
      active: form.active,
      specs: null as string | null,
      metadata: null as string | null,
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Brand is required in BOTH modes: the update DTO's brand_id is nullable and the server
    // re-derives source/brand only when it's present — a blank brand after a network change would
    // save as a silent no-op keeping the old brand. Block submit until a brand is picked.
    if (!form.brand_id) {
      setErrors((prev) => ({ ...prev, brand_id: "Select a brand" }));
      return;
    }

    const specsResult = validateJsonText(form.specs);
    if (!specsResult.ok) {
      setErrors((prev) => ({ ...prev, specs: specsResult.error }));
      return;
    }
    const metadataResult = validateJsonText(form.metadata);
    if (!metadataResult.ok) {
      setErrors((prev) => ({ ...prev, metadata: metadataResult.error }));
      return;
    }

    const payload = buildPayload();
    payload.specs = specsResult.normalized;
    payload.metadata = metadataResult.normalized;

    const schema = isEdit ? updateProductRequestSchema : createProductRequestSchema;
    const result = schema.safeParse(payload);
    if (!result.success) {
      setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
      return;
    }

    await onSubmit(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit ? "Edit Product" : "New Product"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="source_id">Network *</Label>
              <Select value={form.source_id} onValueChange={handleNetworkChange}>
                <SelectTrigger id="source_id" disabled={networksLoading}>
                  <SelectValue
                    placeholder={networksLoading ? "Loading networks..." : "Select a network"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {networks.map((n) => (
                    <SelectItem key={n.source_id} value={String(n.source_id)}>
                      {n.name}
                      {n.active ? "" : " (inactive)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="brand_id">Brand *</Label>
              <Select
                value={form.brand_id}
                onValueChange={(value) => updateField("brand_id", value)}
              >
                <SelectTrigger id="brand_id" disabled={!form.source_id || brandsLoading}>
                  <SelectValue
                    placeholder={
                      !form.source_id
                        ? "Select a network first"
                        : brandsLoading
                          ? "Loading brands..."
                          : "Select a brand"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => (
                    <SelectItem key={b.brand_id} value={String(b.brand_id)}>
                      {b.name}
                      {b.active ? "" : " (inactive)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.brand_id && <p className="text-sm text-destructive">{errors.brand_id}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Product name"
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="external_id">External ID *</Label>
              <Input
                id="external_id"
                value={form.external_id}
                onChange={(e) => updateField("external_id", e.target.value)}
                placeholder="Feed identifier within the brand"
              />
              {errors.external_id && (
                <p className="text-sm text-destructive">{errors.external_id}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="country_code">Country *</Label>
              <Input
                id="country_code"
                value={form.country_code}
                onChange={(e) => updateField("country_code", e.target.value)}
                placeholder="US (ZZ = not geo-segmented)"
                maxLength={2}
              />
              {errors.country_code && (
                <p className="text-sm text-destructive">{errors.country_code}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="language">Language *</Label>
              <Input
                id="language"
                value={form.language}
                onChange={(e) => updateField("language", e.target.value)}
                placeholder="BCP 47 tag, e.g. en-US, fr-CA"
                maxLength={35}
              />
              {errors.language && <p className="text-sm text-destructive">{errors.language}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                placeholder="Product category"
              />
              {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => updateField("price", e.target.value)}
                placeholder="0.00"
              />
              {errors.price && <p className="text-sm text-destructive">{errors.price}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={form.currency}
                onChange={(e) => updateField("currency", e.target.value)}
                placeholder="USD"
                maxLength={3}
              />
              {errors.currency && <p className="text-sm text-destructive">{errors.currency}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="original_price">Original Price</Label>
              <Input
                id="original_price"
                type="number"
                step="0.01"
                min="0"
                value={form.original_price}
                onChange={(e) => updateField("original_price", e.target.value)}
                placeholder="0.00"
              />
              {errors.original_price && (
                <p className="text-sm text-destructive">{errors.original_price}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={form.url}
                onChange={(e) => updateField("url", e.target.value)}
                placeholder="https://example.com/product"
              />
              {errors.url && <p className="text-sm text-destructive">{errors.url}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="tracking_url">Tracking URL</Label>
              <Input
                id="tracking_url"
                type="url"
                value={form.tracking_url}
                onChange={(e) => updateField("tracking_url", e.target.value)}
                placeholder="Pre-wrapped affiliate tracking link (stored as-is)"
              />
              {errors.tracking_url && (
                <p className="text-sm text-destructive">{errors.tracking_url}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                type="url"
                value={form.image_url}
                onChange={(e) => updateField("image_url", e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              {errors.image_url && <p className="text-sm text-destructive">{errors.image_url}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="rating">Rating</Label>
              <Input
                id="rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={form.rating}
                onChange={(e) => updateField("rating", e.target.value)}
                placeholder="0.0 - 5.0"
              />
              {errors.rating && <p className="text-sm text-destructive">{errors.rating}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="rating_count">Rating Count</Label>
              <Input
                id="rating_count"
                type="number"
                min="0"
                value={form.rating_count}
                onChange={(e) => updateField("rating_count", e.target.value)}
                placeholder="0"
              />
              {errors.rating_count && (
                <p className="text-sm text-destructive">{errors.rating_count}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Product description"
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="specs">Specs (JSON)</Label>
              <textarea
                id="specs"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={form.specs}
                onChange={(e) => updateField("specs", e.target.value)}
                placeholder='{"color": "black"}'
              />
              {errors.specs && <p className="text-sm text-destructive">{errors.specs}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="metadata">Metadata (JSON)</Label>
              <textarea
                id="metadata"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={form.metadata}
                onChange={(e) => updateField("metadata", e.target.value)}
                placeholder='{"source_notes": "..."}'
              />
              {errors.metadata && <p className="text-sm text-destructive">{errors.metadata}</p>}
            </div>
          </div>

          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <Switch
                id="in_stock"
                checked={form.in_stock}
                onCheckedChange={(checked) => updateField("in_stock", checked)}
              />
              <Label htmlFor="in_stock">In Stock</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="active"
                checked={form.active}
                onCheckedChange={(checked) => updateField("active", checked)}
              />
              <Label htmlFor="active">Active</Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {isEdit ? "Update Product" : "Create Product"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
